import { Favorite, FavoriteBorder } from '@mui/icons-material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Popover from '@mui/material/Popover';
import React, { useRef } from 'react';
import toast from 'react-hot-toast';
import { Comment } from 'src/api/comments';
import { useScopedBranding } from 'src/branding';
import avatar from 'src/assets/images/logos/default-profile.png';
import styles from '../CommentItem.module.scss';

interface CommentHeaderProps {
  liked: boolean;
  likeCount: number;
  comment: Comment;
  userId?: string;
  timestamp: string;
  anchorEl: HTMLElement | null;
  onLike: () => void;
  onClickMore: (event: React.MouseEvent<HTMLElement>) => void;
  onClosePopover: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const CommentHeader: React.FC<CommentHeaderProps> = ({
  userId,
  comment,
  liked,
  likeCount,
  timestamp,
  anchorEl,
  onLike,
  onClickMore,
  onClosePopover,
  onEdit,
  onDelete,
}) => {
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  // In a branded circle people without a photo show their initial instead of
  // the Dunbar placeholder image
  const { isBranded } = useScopedBranding();

  const handleEdit = () => {
    onClosePopover();
    onEdit();
  };

  const handleDelete = () => {
    onClosePopover();
    onDelete();
  };

  const handleLike = () => {
    if (userId === comment.author.user_id) {
      toast.error('You cannot like your own comment');
      return;
    }

    onLike();
  };

  return (
    <div className={styles.commentHeader}>
      <div className={styles.commentAuthor}>
        {!comment.author.avatar_url && isBranded ? (
          <span
            className={`${styles.avatar} ${styles.avatarInitial}`}
            aria-hidden="true"
          >
            {comment.author.name?.charAt(0).toUpperCase() || '?'}
          </span>
        ) : (
          <img
            src={comment.author.avatar_url || avatar}
            alt={comment.author.name}
            className={styles.avatar}
          />
        )}
        <span className={styles.authorName}>{comment.author.name}</span>
        <span>&bull;</span>
        <span className={styles.date}>{timestamp}</span>
      </div>
      <div className={styles.commentHeaderActions}>
        {userId === comment.author.user_id && !comment.is_deleted && (
          <>
            <button
              ref={moreBtnRef}
              className={styles.moreButton}
              onClick={(e) => onClickMore(e)}
            >
              <MoreHorizIcon sx={{ color: 'black', cursor: 'pointer' }} />
            </button>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              onClose={onClosePopover}
            >
              <div className={styles.popover}>
                <div className={styles.popoverEdit} onClick={handleEdit}>
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  Edit
                </div>
                <div className={styles.popoverDelete} onClick={handleDelete}>
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                  Delete
                </div>
              </div>
            </Popover>
          </>
        )}
        {!comment.is_deleted && (
          <div className={styles.likeButton} onClick={handleLike}>
            {liked ? (
              <Favorite style={{ color: 'red', cursor: 'pointer' }} />
            ) : (
              <FavoriteBorder style={{ color: 'black', cursor: 'pointer' }} />
            )}
            <span className={styles.likeCount}>{likeCount}</span>
          </div>
        )}
      </div>
    </div>
  );
};
